/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import {
    AppLoadingIndicatorV1,
    NotificationTypeV1,
    useNotificationProviderV1,
} from "@startree-ui/platform-ui";
import React, {
    createContext,
    FunctionComponent,
    useContext,
    useEffect,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { ActionStatus } from "../../rest/actions.interfaces";
import { PlatformUser } from "../../rest/dto/user.interfaces";
import { useGetUser } from "../../rest/user/user.actions";
import {
    ByocForbiddenRoutes,
    CloudV2ForbiddenRoutes,
    getBasePath,
    SaasForbiddenRoutes,
} from "../../utils/routes/routes.util";
import {
    isCloudV2User,
    isSaasUser,
    isUserInternalAdmin,
} from "../../utils/user/user.util";
import { useAuthProvider } from "../auth-provider-loader/auth-provider-loader.component";
import {
    UserProviderContextProps,
    UserProviderProps,
} from "./user-provider.interfaces";

export const UserProvider: FunctionComponent<UserProviderProps> = ({
    children,
}) => {
    const { isAuthenticated, user: auth0User } = useAuthProvider();
    const [user, setUser] = useState<PlatformUser>({} as PlatformUser);
    const [isInitial, setIsInitial] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const { notify } = useNotificationProviderV1();
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const [checkPageAccessLoading, setCheckPageAccessLoading] = useState(false);
    const { user: fetchedUser, getUser, status: getUserStatus } = useGetUser();

    useEffect(() => {
        if (isAuthenticated) {
            setLoading(true);
            getUser();
        }

        setIsInitial(false);
    }, [isAuthenticated]);

    useEffect(() => {
        if (getUserStatus === ActionStatus.Done) {
            setLoading(false);
            if (!fetchedUser) {
                setUser({} as PlatformUser);
                notify(NotificationTypeV1.Error, t("message.user-get-null"));

                return;
            }

            setUser(fetchedUser);
        }

        if (getUserStatus === ActionStatus.Error) {
            setLoading(false);
            setUser({} as PlatformUser);
            notify(NotificationTypeV1.Error, t("message.user-get-error"));
        }
    }, [getUserStatus]);

    useEffect(() => {
        if (loading || getUserStatus === ActionStatus.Initial) {
            return;
        }

        setCheckPageAccessLoading(true);

        // if user does not have page access, redirect them to base path
        if (!checkPageAccessAllowed(user)) {
            setCheckPageAccessLoading(false);
            handleUnauthorizedAccess();
        }

        setCheckPageAccessLoading(false);
    }, [location.pathname, loading, user]);

    const checkPageAccessAllowed = (user: PlatformUser): boolean => {
        // Internal admin has access to all the routes
        // hence does not require any route access check
        if (isUserInternalAdmin(user)) {
            return true;
        }

        // cloud v2 user only has assess to new-user route
        if (isCloudV2User(auth0User)) {
            return !matchPath(location.pathname, {
                path: CloudV2ForbiddenRoutes,
            });
        }

        // Saas user does not have access to advance actions
        if (isSaasUser(user)) {
            // if user tries to access forbidden routes then
            // redirect to base page
            if (
                matchPath(location.pathname, {
                    path: SaasForbiddenRoutes,
                })
            ) {
                return false;
            }

            return true;
        }

        // if BYOC user tries to access forbidden routes then
        // redirect to base path
        if (matchPath(location.pathname, { path: ByocForbiddenRoutes })) {
            return false;
        }

        return true;
    };

    const handleUnauthorizedAccess = (): void => {
        history.push({ pathname: getBasePath() });
    };

    const userProviderContext = {
        user: user,
    };

    if (isInitial || loading || checkPageAccessLoading) {
        return <AppLoadingIndicatorV1 />;
    }

    return (
        <UserProviderContext.Provider value={userProviderContext}>
            {children}
        </UserProviderContext.Provider>
    );
};

const UserProviderContext = createContext<UserProviderContextProps>(
    {} as UserProviderContextProps
);

export const useUser = (): UserProviderContextProps => {
    return useContext(UserProviderContext);
};
